import { createFileRoute, useParams } from '@tanstack/react-router';

import { useLtvSummary } from '@/features/ltv';
import { useSyncStatus } from '@/features/sync';
import { AnalysisGuide } from '@/shared/ui/analysis-guide';
import { AnimatedContainer } from '@/shared/ui/animated';
import { MemberOnlyNotice } from '@/shared/ui/member-only-notice';
import { NeedsSyncState } from '@/shared/ui/needs-sync-state';
import { PageHeader } from '@/shared/ui/page-header';
import { testIds } from '@/shared/testing';
import { AppLayout } from '../../-components/app-layout';
import { CohortBreakdown } from './-components/cohort-breakdown';
import { MemberLtvTable } from './-components/member-ltv-table';
import { SummaryCards } from './-components/ltv-summary-cards';

export const Route = createFileRoute('/_authenticated/$mallId/analysis/ltv/')({
  component: LtvPage,
});

function LtvPage() {
  const { mallId } = useParams({ from: '/_authenticated/$mallId/analysis/ltv/' });
  const { data: syncStatus, isLoading: isSyncLoading } = useSyncStatus(mallId);
  const { data: summary, isLoading: isSummaryLoading } = useLtvSummary();

  const needsSync = !isSyncLoading && !syncStatus?.lastSyncAt;

  if (needsSync && !isSummaryLoading) {
    return (
      <AppLayout pageTitle="고객 가치">
        <NeedsSyncState />
      </AppLayout>
    );
  }

  return (
    <AppLayout pageTitle="고객 가치">
      <div className="space-y-6 max-w-6xl mx-auto">
        <AnimatedContainer delay={0}>
          <PageHeader
            title="고객 가치"
            description="회원별 예상 매출과 이탈 위험도를 확인합니다"
            data-testid={testIds.ltv.pageHeader}
            action={
              summary?.lastCalculatedAt && (
                <span className="text-xs text-muted-foreground">
                  마지막 계산: {new Date(summary.lastCalculatedAt).toLocaleString('ko-KR')}
                </span>
              )
            }
          />
        </AnimatedContainer>

        <AnimatedContainer delay={0.05}>
          <MemberOnlyNotice />
        </AnimatedContainer>

        <AnimatedContainer delay={0.1}>
          <AnalysisGuide title="이 분석은 이렇게 읽어요">
            <p className="text-xs text-muted-foreground/70">
              이 분석의 다른 이름은 <strong>LTV(Life Time Value) 분석</strong>이에요.
            </p>
            <p>
              <strong>LTV(생애가치)</strong>는 "이 고객이 앞으로 1년간 얼마나 더 살까?"를 예측한
              금액이에요.
            </p>
            <p>LTV가 높을수록 우리 쇼핑몰에 중요한 고객이고, 낮으면 이탈할 가능성이 높아요.</p>
            <p>
              <strong>이탈 위험도</strong>는 마지막 구매 이후 시간이 얼마나 지났는지를 0~1 사이로
              나타내요.
            </p>
            <div className="mt-2 grid grid-cols-3 gap-2 text-xs">
              <span className="bg-signal-green-soft/60 text-signal-green-text rounded-lg px-2.5 py-1.5">
                0.3 이하: 안전 — 곧 다시 구매할 가능성 높음
              </span>
              <span className="bg-signal-yellow-soft/60 text-signal-yellow-text rounded-lg px-2.5 py-1.5">
                0.3~0.7: 주의 — 쿠폰이나 맞춤 오퍼 추천
              </span>
              <span className="bg-signal-red-soft/60 text-signal-red-text rounded-lg px-2.5 py-1.5">
                0.7 이상: 위험 — 지금 바로 개입이 필요해요
              </span>
            </div>
            <div className="mt-3 pt-3 border-t border-border/50">
              <p className="font-medium text-foreground/80">계산은 이렇게 해요</p>
              <ul className="mt-1.5 space-y-1 text-muted-foreground list-disc list-inside">
                <li>
                  <strong>추정 LTV</strong> = 평균 주문액 × 1년간 예상 주문 횟수. 구매 빈도가
                  높을수록 LTV가 올라가요.
                </li>
                <li>
                  <strong>이탈 위험도</strong> = 마지막 구매 후 경과일 ÷ 평소 구매 주기. 평소보다
                  2배 이상 안 사면 위험도가 거의 1에 가까워져요.
                </li>
                <li>
                  <strong>코호트</strong> = 추정 LTV 기준 상위 25%씩 4단계로 나눠요. 우수 → 성장 →
                  이탈 위험 → 이탈 순이에요.
                </li>
              </ul>
            </div>
          </AnalysisGuide>
        </AnimatedContainer>

        <AnimatedContainer delay={0.2}>
          <SummaryCards />
        </AnimatedContainer>

        <AnimatedContainer delay={0.3}>
          <div>
            <h2 className="text-sm font-semibold mb-3">코호트 분포</h2>
            <CohortBreakdown />
          </div>
        </AnimatedContainer>

        <AnimatedContainer delay={0.4}>
          <MemberLtvTable />
        </AnimatedContainer>
      </div>
    </AppLayout>
  );
}
